import { Error, OpenInNew, Warning } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { WOErrorTokens } from '../../common/enums/enums';
import { getShortOrderNumber } from '../../helpers/orders';
import { GenConfirmationDialog } from '../GenDialogs';

export default function WOConstraintsModal({
  error,
  onDiscard,
  onEditServices,
  onExcludeServiceAndCreate,
  selectedPrices = [],
  onCreateWithAlternative,
  onOverwriteOrder,
  onContinueToCreate,
}) {
  const { t } = useTranslation('global');
  const isMultipleOrderLimitExceed =
    Object.keys(error?.data ?? {}).length === selectedPrices?.length;
  const isSingleService =
    selectedPrices?.length === 1 || isMultipleOrderLimitExceed;
  if (!error || error?.token !== WOErrorTokens.SERVICE_LIMIT_EXCEEDED)
    return null;

  const defaultValues = {
    title: (
      <Trans
        t={t}
        i18nKey="woErrors.titles.DAYS_UNTIL_REORDER"
        values={error?.data?.[0]}
      />
    ),
    titleIcon: Error,
    subtitle: t('woErrors.subTitleSeeWO'),
    confirmText: t('woErrors.confirmTexts.DAYS_UNTIL_REORDER'),
    declineText: t('woErrors.declineTexts.DAYS_UNTIL_REORDER'),
    onConfirm: onEditServices,
    onRefuse: onDiscard,
  };

  const paramsByOverridableAndType = {
    false: {
      DAYS_UNTIL_REORDER: {
        title: (
          <Trans
            t={t}
            i18nKey="woErrors.titles.DAYS_UNTIL_REORDER"
            values={error?.data?.[0]}
          />
        ),
        customTitle: false,
        titleIcon: Error,
        titleColor: 'error',
        titleSx: {},
        subtitle: t('woErrors.subTitleSeeWO'),
        confirmText: t('woErrors.confirmTexts.DAYS_UNTIL_REORDER'),
        declineText: t('woErrors.declineTexts.DAYS_UNTIL_REORDER'),
        onConfirm: onEditServices,
        onRefuse: onDiscard,
      },
      MAX_NUMBER_OF_ORDERS: {
        title: (
          <Trans
            t={t}
            i18nKey="woErrors.titles.MAX_NUMBER_OF_ORDERS"
            values={error?.data?.[0]}
          />
        ),
        customTitle: true,
        titleIcon: Warning,
        titleColor: 'warning',
        titleSx: { fontSize: '100px' },
        subtitle: t('woErrors.subTitleSeeWO'),
        confirmText: !isSingleService
          ? t('woErrors.confirmTexts.MAX_NUMBER_OF_ORDERS')
          : t('woErrors.confirmTexts.DAYS_UNTIL_REORDER'),
        declineText: !isSingleService
          ? t('woErrors.declineTexts.MAX_NUMBER_OF_ORDERS')
          : t('woErrors.declineTexts.DAYS_UNTIL_REORDER'),
        onConfirm: !isSingleService
          ? onExcludeServiceAndCreate
          : onEditServices,
        onRefuse: !isSingleService ? onEditServices : onDiscard,
      },
    },
    true: {},
  };

  const params =
    paramsByOverridableAndType[error?.data?.[0]?.isOverridable][
      error?.data?.[0]?.type
    ] || defaultValues;

  return (
    <GenConfirmationDialog
      open={error.token === WOErrorTokens.SERVICE_LIMIT_EXCEEDED}
      title={
        params.customTitle ? t('woErrors.customTitle') : t('woErrors.title')
      }
      titleIcon={params.titleIcon}
      titleIconColor={params.titleColor}
      closeText={params.declineText}
      confirmText={params.confirmText}
      onConfirm={params.onConfirm}
      onRefuse={params.onRefuse}
      titleSx={params.titleSx}
    >
      <Stack
        gap={2}
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Typography variant="body1" color="text.secondary">
          {params.title}
        </Typography>
        <Typography color="text.secondary">{params.subtitle}</Typography>
        <Stack direction="row" alignItems="center">
          <Typography variant="overline">{t('workOrderNumber')}:</Typography>
          <Button
            endIcon={<OpenInNew />}
            href={`/orders/${error?.data?.[0]?.vendorOrderId}`}
            target="_blank"
          >
            {getShortOrderNumber(error?.data?.[0]?.woNumber)}
          </Button>
        </Stack>
      </Stack>
    </GenConfirmationDialog>
  );
}
